import { useCallback, useEffect, useState, useRef } from 'react';
import {
  addGroupMember,
  actionGroupJoinRequest,
  approveLedgerEntry,
  GroupContext,
  getFundInvestmentGroupContext,
  getMemberSuggestions,
  listGroupMembers,
  listGroupJoinRequests,
  raiseGroupJoinRequest,
  reorderGroupMembers,
  removeGroupMember,
  toggleOrderChecked
} from 'api/fundInvestmentManagement';
import { BuyRequestRow, GroupMemberRow, mapMemberRecord } from './types';

function toMessage(error: unknown) {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as any).response;
    return response?.data?.message || response?.data?.error || response?.data?.title || 'Request failed';
  }

  if (error instanceof Error) return error.message;
  return 'Request failed';
}

export function useGroupReport() {
  const [context, setContext] = useState(() => getFundInvestmentGroupContext());
  const [members, setMembers] = useState<GroupMemberRow[]>([]);
  const [buyRequests, setBuyRequests] = useState<BuyRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [memberSuggestions, setMemberSuggestions] = useState<
    Array<{ memberEmail: string; memberName: string; memberAvatar?: string }>
  >([]);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loadingOrderChecked, setLoadingOrderChecked] = useState<Set<string>>(new Set());
  const suggestionsCache = useRef<Record<string, any[]>>({});

  useEffect(() => {
    const syncContext = () => setContext(getFundInvestmentGroupContext());

    syncContext();
    window.addEventListener('storage', syncContext);

    return () => window.removeEventListener('storage', syncContext);
  }, []);

  const resolveLatestContext = useCallback((): GroupContext | null => {
    const latest = getFundInvestmentGroupContext();

    if (latest) {
      setContext((current) => {
        if (!current) return latest;
        return current.groupId === latest.groupId && current.serviceId === latest.serviceId ? current : latest;
      });
      return latest;
    }

    return context;
  }, [context]);

  const refresh = useCallback(async () => {
    const activeContext = resolveLatestContext();

    if (!activeContext) {
      setError('Missing group context. Please reselect a group and service.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [memberRowsResult, joinRequestsResult] = await Promise.allSettled([
        listGroupMembers(activeContext),
        listGroupJoinRequests(activeContext, 'pending')
      ]);

      if (memberRowsResult.status === 'fulfilled') {
        setMembers(memberRowsResult.value.map(mapMemberRecord));
      } else {
        setMembers([]);
      }

      if (joinRequestsResult.status === 'fulfilled') {
        setBuyRequests(joinRequestsResult.value as BuyRequestRow[]);
      } else {
        setBuyRequests([]);
      }

      setPermissionDenied(false);
    } catch (caughtError: unknown) {
      const status = (caughtError as any)?.response?.status;

      if (status === 403) {
        setPermissionDenied(true);
        setError('Permission denied for admin actions.');
      } else {
        setError(toMessage(caughtError));
      }
    } finally {
      setLoading(false);
    }
  }, [resolveLatestContext]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const addMember = useCallback(
    async (payload: { memberEmail: string; memberName: string; memberAvatar?: string }) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      try {
        await addGroupMember({ ...activeContext, ...payload });
        await refresh();
      } catch (caughtError: unknown) {
        const status = (caughtError as any)?.response?.status;
        if (status === 403) setPermissionDenied(true);
        throw new Error(toMessage(caughtError));
      }
    },
    [refresh, resolveLatestContext]
  );

  const removeMemberAction = useCallback(
    async (memberEmail: string) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      try {
        await removeGroupMember({ ...activeContext, memberEmail });
        await refresh();
      } catch (caughtError: unknown) {
        const status = (caughtError as any)?.response?.status;
        if (status === 403) setPermissionDenied(true);
        throw new Error(toMessage(caughtError));
      }
    },
    [refresh, resolveLatestContext]
  );

  const reorderMembersAction = useCallback(
    async (orderUpdates: Array<{ memberEmail: string; order: number }>) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      const currentGroupMemberEmails = new Set(members.map((member) => member.memberEmail));
      const scopedOrderUpdates = orderUpdates.filter((update) =>
        currentGroupMemberEmails.has(update.memberEmail)
      );

      if (scopedOrderUpdates.length === 0) return;

      try {
        await reorderGroupMembers({ ...activeContext, orderUpdates: scopedOrderUpdates });
        await refresh();
      } catch (caughtError: unknown) {
        const status = (caughtError as any)?.response?.status;
        if (status === 403) setPermissionDenied(true);
        throw new Error(toMessage(caughtError));
      }
    },
    [members, refresh, resolveLatestContext]
  );

  const reviewRequestAction = useCallback(
    async (payload: {
      requestId: string;
      action: 'approved' | 'rejected';
      actionNote?: string;
    }) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      try {
        await actionGroupJoinRequest({
          ...activeContext,
          requestId: payload.requestId,
          action: payload.action,
          actionNote: payload.actionNote
        });

        await refresh();
      } catch (caughtError: unknown) {
        const status = (caughtError as any)?.response?.status;
        if (status === 403) {
          setPermissionDenied(true);
          throw new Error('Permission denied for request approval actions.');
        }
        throw new Error(toMessage(caughtError));
      }
    },
    [refresh, resolveLatestContext]
  );

  const raiseJoinRequestAction = useCallback(
    async (payload: { groupId: string; serviceId?: string; requestMessage?: string; message?: string }) => {
      try {
        await raiseGroupJoinRequest({
          groupId: payload.groupId,
          serviceId: payload.serviceId,
          requestMessage: payload.requestMessage,
          message: payload.message
        });
      } catch (caughtError: unknown) {
        throw new Error(toMessage(caughtError));
      }
    },
    []
  );

  const toggleLedgerApproval = useCallback(
    async (payload: {
      unitType: 'incomeUnits' | 'growthUnits';
      transactionDate: string;
      expenseId: string;
      isApproved: boolean;
    }) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      try {
        await approveLedgerEntry({
          ...activeContext,
          unitType: payload.unitType,
          transactionDate: payload.transactionDate,
          expenseId: payload.expenseId,
          isApproved: payload.isApproved
        });
      } catch (caughtError: unknown) {
        throw new Error(toMessage(caughtError));
      }
    },
    [resolveLatestContext]
  );

  const toggleOrderCheckedAction = useCallback(
    async (member: { memberEmail: string; memberName: string; orderChecked?: boolean }) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      const newOrderChecked = !member.orderChecked;

      setLoadingOrderChecked((prev) => {
        const next = new Set(prev);
        next.add(member.memberEmail);
        return next;
      });

      try {
        const result = await toggleOrderChecked({
          ...activeContext,
          memberEmail: member.memberEmail,
          orderChecked: newOrderChecked
        });

        // Update the members list with the returned member data
        if (result && result.member) {
          setMembers((prev) =>
            prev.map((m) =>
              m.memberEmail === member.memberEmail
                ? { ...m, orderChecked: result.member.orderChecked ?? false }
                : m
            )
          );
        }
      } catch (caughtError: unknown) {
        const status = (caughtError as any)?.response?.status;
        if (status === 403) setPermissionDenied(true);
        throw new Error(toMessage(caughtError));
      } finally {
        setLoadingOrderChecked((prev) => {
          const next = new Set(prev);
          next.delete(member.memberEmail);
          return next;
        });
      }
    },
    [resolveLatestContext]
  );

  const loadMemberSuggestions = useCallback(
    async (search?: string) => {
      const activeContext = resolveLatestContext();
      if (!activeContext) return;

      const cacheKey = search || '';
      if (suggestionsCache.current[cacheKey]) {
        setMemberSuggestions(suggestionsCache.current[cacheKey]);
        return;
      }

      setLoadingSuggestions(true);
      setSuggestionsError(null);

      try {
        const suggestions = await getMemberSuggestions(activeContext, { search, limit: 100 });
        suggestionsCache.current[cacheKey] = suggestions;
        setMemberSuggestions(suggestions);
      } catch (caughtError: unknown) {
        const status = (caughtError as any)?.response?.status;
        if (status === 403) {
          setPermissionDenied(true);
          setSuggestionsError('Permission denied for member suggestions.');
          setMemberSuggestions([]);
        } else {
          setSuggestionsError(toMessage(caughtError));
        }
      } finally {
        setLoadingSuggestions(false);
      }
    },
    [resolveLatestContext]
  );

  return {
    context,
    members,
    buyRequests,
    memberSuggestions,
    loadingSuggestions,
    suggestionsError,
    loading,
    error,
    permissionDenied,
    loadingOrderChecked,
    refresh,
    loadMemberSuggestions,
    reviewRequest: reviewRequestAction,
    raiseJoinRequest: raiseJoinRequestAction,
    toggleLedgerApproval,
    toggleOrderChecked: toggleOrderCheckedAction,
    addMember: addMember,
    removeMember: removeMemberAction,
    reorderMembers: reorderMembersAction
  };
}